import React, { useEffect, useState } from "react";
import {
  listSignalTypes,
  createSignalType,
  updateSignalType,
  deleteSignalType,
} from "../api/signalTypes";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Input } from "../ui/input";

export default function SignalSettings() {
  const [signalTypes, setSignalTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", name_short: "", unit: "" });
  const [error, setError] = useState("");

  const loadData = async () => {
    setLoading(true);
    try {
      const data = await listSignalTypes();
      setSignalTypes(data || []);
    } catch (err) {
      console.error("Failed to load signal types", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const openModal = (record = null) => {
    setEditing(record);
    setForm(
      record
        ? { name: record.name, name_short: record.name_short, unit: record.unit || "" }
        : { name: "", name_short: "", unit: "" }
    );
    setModalOpen(true);
    setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    const payload = {
      name: form.name.trim(),
      name_short: form.name_short.trim(),
      unit: form.unit.trim() || null,
    };

    if (!payload.name || !payload.name_short) {
      setError("Name and short code are required.");
      return;
    }

    try {
      if (editing) {
        await updateSignalType(editing.id, payload);
      } else {
        await createSignalType(payload);
      }
      setModalOpen(false);
      await loadData();
    } catch (err) {
      console.error("Failed to save signal type", err);
      setError(err.response?.data?.detail || "Unable to save changes.");
    }
  };

  const handleDelete = async (record) => {
    if (!window.confirm(`Delete signal type "${record.name}"?`)) return;
    try {
      await deleteSignalType(record.id);
      await loadData();
    } catch (err) {
      console.error("Failed to delete signal type", err);
      setError("Unable to delete signal type. Check if it is referenced by signal matches.");
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Signal Settings</h1>
          <p className="text-slate-600">
            Define the telemetry types the SCADA import expects. Each type exposes a short code (used in CSV tag names)
            and an optional engineering unit.
          </p>
        </div>
        <Button onClick={() => openModal()} className="w-full md:w-auto">
          + New signal type
        </Button>
      </div>

      <div className="rounded-xl border bg-white shadow-sm overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b bg-slate-50">
          <span className="font-semibold text-slate-800">Registered signal types</span>
          <span className="text-xs text-slate-500">
            {loading ? "Loading…" : `${signalTypes.length} type${signalTypes.length === 1 ? "" : "s"}`}
          </span>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-100 text-slate-600">
              <tr>
                <th className="text-left px-4 py-3">Name</th>
                <th className="text-left px-4 py-3">Short code</th>
                <th className="text-left px-4 py-3">Unit</th>
                <th className="text-left px-4 py-3">Usage</th>
                <th className="text-right px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={5} className="px-4 py-6 text-center text-slate-500">
                    Loading signal types…
                  </td>
                </tr>
              )}
              {!loading && signalTypes.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-4 py-6 text-center text-slate-500">
                    No signal types yet. Click “New signal type” to add one.
                  </td>
                </tr>
              )}
              {!loading &&
                signalTypes.map((st) => (
                  <tr key={st.id} className="border-t hover:bg-slate-50">
                    <td className="px-4 py-3">
                      <div className="font-medium text-slate-900">{st.name}</div>
                      <div className="text-xs text-slate-500">ID {st.id}</div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center px-2 py-1 rounded-md bg-blue-50 text-blue-700 text-xs font-medium">
                        {st.name_short}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-slate-600">{st.unit || "—"}</td>
                    <td className="px-4 py-3 text-slate-500">Used by SCADA imports to match the CSV 'tid' column.</td>
                    <td className="px-4 py-3 text-right space-x-2">
                      <button
                        className="text-blue-600 hover:underline"
                        onClick={() => openModal(st)}
                      >
                        Edit
                      </button>
                      <button className="text-rose-600 hover:underline" onClick={() => handleDelete(st)}>
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={modalOpen}>
        <div className="w-[440px]">
          <DialogHeader>
            <DialogTitle>{editing ? `Edit ${editing.name}` : "Create signal type"}</DialogTitle>
          </DialogHeader>
          <DialogContent>
            <form className="space-y-4" onSubmit={handleSubmit}>
              <div>
                <label className="text-sm font-medium text-slate-600 mb-1 block">Display name</label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                  placeholder="E.g. Active Power"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-600 mb-1 block">Short code</label>
                <Input
                  value={form.name_short}
                  onChange={(e) => setForm((prev) => ({ ...prev, name_short: e.target.value.toUpperCase() }))}
                  placeholder="E.g. PAC"
                />
                <p className="text-xs text-slate-500 mt-1">
                  This must match the SCADA tag prefix (unique, 10 characters max).
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-600 mb-1 block">Unit (optional)</label>
                <Input
                  value={form.unit}
                  onChange={(e) => setForm((prev) => ({ ...prev, unit: e.target.value }))}
                  placeholder="E.g. kW"
                />
              </div>

              {error && <div className="text-sm text-rose-600">{error}</div>}

              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  className="px-4 py-2 rounded-md border text-slate-600"
                  onClick={() => setModalOpen(false)}
                >
                  Cancel
                </button>
                <Button type="submit">{editing ? "Save changes" : "Create type"}</Button>
              </div>
            </form>
          </DialogContent>
        </div>
      </Dialog>
    </div>
  );
}
